import { type FC, type ReactNode, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { Link } from 'dibs-elements/exports/Link.tsx';
import { getSrcSetStringXDescriptors } from 'dibs-image-utils/exports/srcSet.ts';

import Photo from 'dibs-icons/exports/Photo.ts';
import PlayArrow from 'dibs-icons/exports/PlayArrow.ts';
import ArtworkFrame from 'dibs-icons/exports/legacy/ArtworkFrame.ts';

import { trackThumbnailClick } from '../../respShared/PdpRespSharedHeroCarousel/tracking.ts';

import { type HeroCarouselThumb_sortedPhoto$key } from './__generated__/HeroCarouselThumb_sortedPhoto.graphql.ts';
import { type HeroCarouselThumb_videoVimeo$key } from './__generated__/HeroCarouselThumb_videoVimeo.graphql.ts';
import { type HeroCarouselThumb_viewInRoomPhoto$key } from './__generated__/HeroCarouselThumb_viewInRoomPhoto.graphql.ts';

import styles from './HeroCarouselThumb.scss';

const sortedPhotoFragment = graphql`
    fragment HeroCarouselThumb_sortedPhoto on ItemPhoto {
        masterOrZoomPath
        alt
    }
`;

const videoVimeoFragment = graphql`
    fragment HeroCarouselThumb_videoVimeo on VideoVimeo {
        thumbnail(size: w100h75, includePlayButton: false, pad: false)
    }
`;

const viewInRoomPhotoFragment = graphql`
    fragment HeroCarouselThumb_viewInRoomPhoto on viewInRoomPhotos {
        src
        credits
    }
`;

export const HeroCarouselThumb: FC<{
    type: 'photo' | 'video' | 'viewInRoom' | 'requestMore';
    index: number;
    isActive: boolean;
    totalSlides: number;
    onSelect: (index: number) => void;
    onHover: (index: number) => void;
    sortedPhoto: HeroCarouselThumb_sortedPhoto$key | null;
    videoVimeo: HeroCarouselThumb_videoVimeo$key | null;
    viewInRoomPhoto: HeroCarouselThumb_viewInRoomPhoto$key | null;
}> = ({
    type,
    index,
    isActive,
    sortedPhoto: sortedPhotoRef,
    videoVimeo: videoVimeoRef,
    viewInRoomPhoto: viewInRoomPhotoRef,
    totalSlides,
    onSelect,
    onHover,
}) => {
    const sortedPhoto = useFragment(sortedPhotoFragment, sortedPhotoRef);
    const videoVimeo = useFragment(videoVimeoFragment, videoVimeoRef);
    const viewInRoomPhoto = useFragment(viewInRoomPhotoFragment, viewInRoomPhotoRef);

    const handleHover = useCallback(() => {
        onHover(index);
    }, [onHover, index]);

    const handleClick = useCallback(() => {
        trackThumbnailClick({
            activeSlide: index + 1,
            totalSlides,
            slideType: type,
        });
        onSelect(index);
    }, [onSelect, index, totalSlides, type]);

    let child: ReactNode = null;
    let extraClassName = '';

    if (type === 'photo' && sortedPhoto) {
        const { masterOrZoomPath, alt } = sortedPhoto;
        const srcSet = getSrcSetStringXDescriptors({
            url: masterOrZoomPath,
            width: 200,
            quality: 60,
        });
        child =
            masterOrZoomPath && alt && srcSet ? (
                <img
                    className={classnames(
                        dibsCss.block,
                        dibsCss.wFull,
                        dibsCss.hFull,
                        dibsCss.objectCover,
                        styles.image
                    )}
                    src={masterOrZoomPath}
                    alt={alt}
                    srcSet={srcSet}
                />
            ) : null;
    } else if (type === 'video' && videoVimeo) {
        const src = videoVimeo?.thumbnail;
        child = src ? (
            <>
                <img
                    className={classnames(
                        dibsCss.block,
                        dibsCss.wFull,
                        dibsCss.hFull,
                        dibsCss.objectCover,
                        styles.image
                    )}
                    src={src}
                    alt=""
                />
                <div
                    className={classnames(
                        dibsCss.absolute,
                        dibsCss.inset0,
                        dibsCss.wFull,
                        dibsCss.hFull,
                        dibsCss.flex,
                        dibsCss.justifyCenter,
                        dibsCss.itemsCenter
                    )}
                >
                    <PlayArrow variation="36CircleFilled" />
                </div>
            </>
        ) : null;
    } else if (type === 'viewInRoom' && viewInRoomPhoto) {
        const { src, credits } = viewInRoomPhoto;
        const srcSet = getSrcSetStringXDescriptors({ url: src, width: 200, quality: 60 });
        child =
            src && credits && srcSet ? (
                <>
                    <img
                        className={classnames(
                            dibsCss.block,
                            dibsCss.wFull,
                            dibsCss.hFull,
                            dibsCss.objectCover,
                            styles.image
                        )}
                        src={src}
                        alt={credits}
                        srcSet={srcSet}
                    />
                    <div
                        className={classnames(
                            dibsCss.absolute,
                            dibsCss.inset0,
                            dibsCss.flex,
                            dibsCss.justifyCenter,
                            dibsCss.itemsCenter,
                            styles.viewInRoomOverlay
                        )}
                    >
                        <ArtworkFrame
                            className={classnames(dibsCss.fillSatan, styles.viewInRoomIcon)}
                        />
                    </div>
                </>
            ) : null;
    } else if (type === 'requestMore') {
        extraClassName = classnames(
            dibsCss.flex,
            dibsCss.itemsCenter,
            dibsCss.justifyCenter,
            dibsCss.textNoir
        );
        child = <Photo variation="40" />;
    }

    return (
        <Link
            className={classnames(
                dibsCss.relative,
                dibsCss.roundedXxsmall,
                dibsCss.overflowHidden,
                dibsCss.shrink0,
                dibsCss.cursorPointer,
                styles.thumb,
                extraClassName,
                { [styles.active]: isActive }
            )}
            onMouseEnter={handleHover}
            onClick={handleClick}
            dataTn="carousel-thumb"
        >
            {child}
        </Link>
    );
};
